import { Link as RouterLink, useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon, SquarePenIcon } from "lucide-react";
import { forwardRef, useEffect } from "react";

import { trpc } from "@blis/trpc/react";

import { CommandMenu } from "@/components/CommandMenu";
import { ErrorPage } from "@/components/ErrorPage";
import { ActiveHoldIndicator, StatIndicator } from "@/components/Indicators";
import { Link } from "@/components/Link";
import { LoadingPage } from "@/components/LoadingPage";
import { PermissionGate } from "@/components/PermissionGate";
import { RoleCondition } from "@/components/RoleCondition";
import { SubHeader } from "@/components/SubHeader";
import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import { Centered } from "@/components/ui/container";
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateHeading,
  EmptyStateIcon,
} from "@/components/ui/empty-state";
import { StatusBadge } from "@/components/ui/icons";
import { NavList, NavListItem } from "@/components/ui/nav-list";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { AccessionCommands } from "@/features/accession/components/AccessionCommands";
import { AccessionLayout } from "@/features/accession/components/AccessionLayout";
import { AccessionProvider } from "@/features/accession/components/AccessionProvider";
import { AccessionShortcuts } from "@/features/accession/components/AccessionShortcuts";
import {
  AccessionSideDetail,
  AccessionSideDetailButton,
} from "@/features/accession/components/AccessionSideDetail";
import { ActionButton } from "@/features/accession/components/ActionButton";
import { ActionDropdown } from "@/features/accession/components/ActionDropdown";
import { BottomTabs } from "@/features/accession/components/detail/BottomTabs";
import { HoldSection } from "@/features/accession/components/detail/HoldSection";
import { InstrumentResultsSection } from "@/features/accession/components/detail/InstrumentResultsSection";
import { ReportSection } from "@/features/accession/components/detail/ReportSection";
import { SamplesSection } from "@/features/accession/components/detail/SampleSection";
import { TestOrdersSection } from "@/features/accession/components/detail/TestOrderSection";
import type { SubjectRouter, TSubjectDetail } from "@/lib/types";
import { format } from "@/lib/utils/date";
import { SubjectCommands } from "./SubjectCommands";
import { MultiColumnView } from "./SubjectMultiColumn";
import { useSubject } from "./SubjectProvider";

export function AccessionView({ accessionId }: { accessionId?: number }) {
  const { subject } = useSubject();
  const accessions = trpc.subjects.accessions.useQuery(subject.id);

  const isLoadingOrNotEmpty =
    accessions.isLoading || (accessions.data && accessions.data.length > 0);

  return isLoadingOrNotEmpty ? (
    <MultiColumnView
      isDetailView={!!accessionId}
      nav={
        <NavList
          items={accessions.data || []}
          autoFocus
          className="divide-y"
          isLoading={accessions.isPending}
          loadingItems={5}
          LoadingComponent={() => (
            <div className="flex flex-col gap-2 px-4 h-20 justify-center ">
              <div className="flex gap-2 w-full justify-between">
                <Skeleton className="w-20" />
                <Skeleton className="w-24" />
              </div>
              <div className="flex gap-2 w-full justify-between">
                <Skeleton className="w-20" />
                <Skeleton className="w-12" />
              </div>
            </div>
          )}
        >
          {(accession) => (
            <NavListItem key={accession.id} asChild>
              <AccessionViewItem
                key={accession.id}
                accession={accession}
                subject={subject}
              />
            </NavListItem>
          )}
        </NavList>
      }
    >
      {accessionId ? (
        <AccessionDetail id={accessionId} />
      ) : (
        <Centered>
          <Text muted>Select an accession to view</Text>
        </Centered>
      )}
    </MultiColumnView>
  ) : (
    <EmptyState>
      <EmptyStateIcon>
        <SquarePenIcon />
      </EmptyStateIcon>
      <EmptyStateHeading>No accessions found</EmptyStateHeading>
      <EmptyStateDescription>
        No accessions found for this subject
      </EmptyStateDescription>
      <EmptyStateActions>
        <RouterLink to="/accessions/new">Create One</RouterLink>
      </EmptyStateActions>
    </EmptyState>
  );
}

export function AccessionDetail(props: { id: number }) {
  const { subject } = useSubject();
  const navigate = useNavigate();
  const { data, isPending, error } = trpc.accession.get.useQuery(props);
  const loadedSubjectId = data?.subjectId;

  useEffect(() => {
    if (loadedSubjectId === undefined || loadedSubjectId === subject.id) {
      return;
    }

    // Keep subject-scoped accession URLs canonical after an accession is moved.
    navigate({
      to: "/subjects/$id/accession",
      params: { id: loadedSubjectId },
      search: { accessionId: props.id },
      replace: true,
    });
  }, [loadedSubjectId, navigate, props.id, subject.id]);

  if (isPending) return <LoadingPage />;
  if (error) return <ErrorPage error={error} />;
  if (!data) return <ErrorPage error={{ message: "Accession not found" }} />;

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <AccessionProvider accession={data}>
        <AccessionLayout
          className="h-full"
          header={
            <SubHeader>
              <SubHeader.Left
                action={
                  <RouterLink
                    to="/subjects/$id/accession"
                    params={{ id: data.subjectId }}
                    preload={false}
                    replace
                    className={buttonVariants({
                      variant: "ghost",
                      size: "icon",
                      className: "flex md:hidden",
                    })}
                  >
                    <ArrowLeftIcon data-slot="icon" />
                  </RouterLink>
                }
              >
                <Link to="/accessions/$id" params={{ id: data.id }}>
                  {data.id}
                </Link>
              </SubHeader.Left>
              <SubHeader.Right className="gap-2">
                <RoleCondition
                  roles={[
                    "blis_lab_manager",
                    "blis_lab_tech",
                    "blis_lab_assistant",
                  ]}
                >
                  <ActionButton size="sm" variant="outline" />
                </RoleCondition>
                <div className="xl:hidden">
                  <AccessionSideDetailButton />
                </div>
                <RoleCondition
                  roles={[
                    "blis_lab_manager",
                    "blis_lab_tech",
                    "blis_lab_assistant",
                  ]}
                >
                  <ActionDropdown />
                </RoleCondition>
              </SubHeader.Right>
            </SubHeader>
          }
          sideDetail={<AccessionSideDetail />}
          sideDetailProps={{ className: "lg:hidden xl:block" }}
        >
          <HoldSection />
          <PermissionGate requiredPermission="read:instrument_results">
            <InstrumentResultsSection />
          </PermissionGate>
          <ReportSection />
          <TestOrdersSection />
          <SamplesSection />
          <BottomTabs />
        </AccessionLayout>
        <AccessionShortcuts />
        <CommandMenu>
          <SubjectCommands />
          <AccessionCommands />
        </CommandMenu>
      </AccessionProvider>
    </div>
  );
}

const AccessionViewItem = forwardRef<
  HTMLAnchorElement,
  { accession: SubjectRouter["accessions"][0]; subject: TSubjectDetail }
>(({ subject, accession }, ref) => {
  const hasHold = accession._count.holds > 0;
  return (
    <RouterLink
      ref={ref}
      className="flex flex-col gap-2 px-4 h-20 justify-center outline-hidden focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring focus-visible:rounded"
      to="/subjects/$id/accession"
      params={{ id: subject.id }}
      activeProps={{ className: "bg-accent-muted" }}
      preload={false}
      replace
      search={(prev) => ({
        ...prev,
        accessionId: accession.id,
      })}
    >
      <div className="flex gap-2 w-full items-center justify-between">
        <Text size="1" mono bold>
          {accession.id}
        </Text>
        <div className="flex items-center gap-1">
          {accession.priority === "STAT" && <StatIndicator />}
          {hasHold && <ActiveHoldIndicator />}
          <StatusBadge status={accession.status} />
        </div>
      </div>
      <div className="flex gap-2 w-full items-center justify-between">
        <Badge>{accession.testType}</Badge>
        <Text size="1" muted mono>
          {format(accession.date)}
        </Text>
      </div>
    </RouterLink>
  );
});
